import React, { useEffect, useState } from 'react';
import {
  List as MuiList,
  ListItem,
  ListItemIcon,
  ListItemText as MuiListItemText,
  Radio
} from '@material-ui/core';
import { styled } from '@this/constants/themes';
import { useInAdvanceApplicationStore } from '@this/src/domain/in_advance/in_advance_application_store';
import { ModalBody, ModalFooter, ModalHeader } from '@this/shared/ui/feedbacks/modal';
import { Button } from '@this/shared/ui/inputs/button';
import { Accordion } from '@this/shared/ui/data_displays/accordion';

interface Props {
  onClose: () => void;
}

const BulkActionsReject: React.FC<Props> = ({ onClose }) => {
  const store = useInAdvanceApplicationStore();
  const { messageTemplates, batchErrors } = store;
  const [expanded, setExpanded] = useState(false);
  const [template, setTemplate] = useState<number | 'input' | null>(null);
  const [message, setMessage] = useState('');

  const handleClick = () => {
    store.fetchBatchReject({
      onSuccess: () => onClose(),
      reject_message:
        template === 'input' ? message : messageTemplates?.find(mt => mt.id === template)?.message || ''
    });
  };

  useEffect(() => {
    store.fetchMessageTemplates();
  }, [store]);

  return (
    <>
      <ModalHeader>申請の一括却下をしますか</ModalHeader>
      <ModalBody>
        <ModalText>
          選択した申請を一括で却下します。
          <br />
          却下事由を選択してください。
        </ModalText>
        <ModalInnerBody>
          <Select
            expanded={expanded}
            onChange={bool => setExpanded(bool)}
            summary={
              <SelectSummary className={expanded || template === null ? 'selected' : undefined}>
                {expanded || template === null
                  ? '却下事由を選択する'
                  : template === 'input'
                  ? '自由入力'
                  : messageTemplates?.find(mt => mt.id === template)?.message}
              </SelectSummary>
            }
          >
            <List>
              {messageTemplates?.map(messageTemplate => (
                <ListItem key={messageTemplate.id} button onClick={() => setTemplate(messageTemplate.id)}>
                  <ListItemIcon>
                    <Radio color="primary" checked={template === messageTemplate.id} />
                  </ListItemIcon>
                  <ListItemText primary={messageTemplate.message} />
                </ListItem>
              ))}
              <ListItem button onClick={() => setTemplate('input')}>
                <ListItemIcon>
                  <Radio color="primary" checked={template === 'input'} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    template === 'input' ? (
                      <>
                        <TextareaLabel>自由入力</TextareaLabel>
                        <Textarea rows={3} value={message} onChange={e => setMessage(e.target.value)} />
                      </>
                    ) : (
                      '自由入力'
                    )
                  }
                />
              </ListItem>
            </List>
          </Select>
        </ModalInnerBody>
        {!expanded && template === 'input' && (
          <ModalInnerBody>
            <Textarea rows={3} value={message} onChange={e => setMessage(e.target.value)} />
          </ModalInnerBody>
        )}
        {batchErrors.length > 0 && (
          <Errors>
            {batchErrors.map((error, i) => (
              <Error key={`error-${i}`}>{error}</Error>
            ))}
          </Errors>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="sub" onClick={onClose}>
          戻る
        </Button>
        <Button onClick={handleClick}>却下する</Button>
      </ModalFooter>
    </>
  );
};

const ModalText = styled.div``;

const ModalInnerBody = styled.div`
  max-width: 420px;
  width: 100%;
  margin: 0 auto;
  margin-top: 16px;
`;

const Select = styled(Accordion)`
  .MuiCollapse-wrapperInner {
    padding: 0;
  }
`;

const SelectSummary = styled.div`
  white-space: pre-wrap;

  &.selected {
    font-weight: bold;
  }
`;

const List = styled(MuiList)`
  padding: 0;

  .MuiListItem-root.Mui-focusVisible {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const ListItemText = styled(MuiListItemText)`
  white-space: pre-wrap;
`;

const Textarea = styled.textarea`
  height: auto;
`;

const TextareaLabel = styled.div`
  margin-bottom: 4px;
`;

const Errors = styled.div`
  margin-top: 16px;
`;

const Error = styled.div`
  color: ${props => props.theme.redColor};
`;

export default BulkActionsReject;
